/** Utilitários do sumário — saíram do `PDFContentArea` porque o layout, o painel
 * de destaques e a navegação usam os três, e nenhum deles deve depender do
 * componente que desenha a página.
 */
import type { BookAccess, PdfTocEntry } from '@/types/api'

/** Achata o sumário — subseções também são alvos de liberação. */
export function flattenToc(toc: PdfTocEntry[]): PdfTocEntry[] {
  return toc.flatMap((entry) => [entry, ...flattenToc(entry.children ?? [])])
}

/** Seção do TOC que contém a página; a MAIS específica vence. */
export function sectionForPage(toc: PdfTocEntry[], page: number): PdfTocEntry | null {
  let melhor: PdfTocEntry | null = null
  for (const entry of flattenToc(toc)) {
    if (page >= entry.page_start && page <= entry.page_end) {
      const atual = entry.page_end - entry.page_start
      if (!melhor || atual <= melhor.page_end - melhor.page_start) melhor = entry
    }
  }
  return melhor
}

/** A seção está liberada para este aluno? */
export function isSectionReleased(access: BookAccess, secao: PdfTocEntry | null): boolean {
  if (access.full_access) return true
  return secao !== null && access.released_locators.includes(secao.locator)
}

/** A página está liberada para este aluno? (deny-by-default — Fase 04) */
export function isPageReleased(access: BookAccess, page: number): boolean {
  if (access.full_access) return true
  return isSectionReleased(access, sectionForPage(access.toc, page))
}

/** Seção anterior e próxima em relação à página corrente, para a barra inferior. */
export function adjacentSections(
  toc: PdfTocEntry[],
  page: number,
): { anterior: PdfTocEntry | null; proxima: PdfTocEntry | null } {
  const ordenadas = flattenToc(toc)
    .slice()
    .sort((a, b) => a.page_start - b.page_start)
  let anterior: PdfTocEntry | null = null
  let proxima: PdfTocEntry | null = null
  for (const secao of ordenadas) {
    if (secao.page_end < page) anterior = secao
    if (secao.page_start > page && proxima === null) proxima = secao
  }
  return { anterior, proxima }
}
